import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getRaidEntityApi } from '../services/getRaidEntityApi';
import { cloneDeep, isEqual, transform, isObject, isArray } from 'lodash';
import {
  IArtemisAttachment,
  IArtemisInitPayload,
} from 'src/manageAction/interfaces/ArtemisAction';
import { SURVEY_ATTACHMENT_VALUE } from 'src/manageAction/interfaces/ManageAction';
import { UpdateActionApiStatus } from 'src/manageAction/utils/manageActionConstants';
import { WorkItemUpdate } from 'src/Models/HistoryItem';
import { Related } from 'src/Models/Related';
import { IRecommendedTags } from 'src/Models/ArtimisActionData';
import { UATConstants } from 'src/Common/Models/UATConstants';
import { IGetWorkloadResponse, INBARFormData, INBARFormMandateFields, INBARFormValidation, INBARNonEditableFormData, INBARWorkloadTableStoreData, IRecommendedOfferItem } from 'src/manageAction/components/molecules/AMM/NBAR/type';
// Validation Automation : Start
import {
  IFieldValidation,
  IUpdateValidationResponse,
  IValidationResponse,
  validationAutomationApi,
} from 'src/manageAction/store/services/ValidationAutomationApi';
// Validation Automation : End

export interface IROBDetailRHFContext {
  values: any;
  formIsDirty: boolean;
  formIsValid: boolean;
  formState: any;
}

export interface ISGSDetailRHFContext {
  values: any;
  formIsDirty: boolean;
  formIsValid: boolean;
  formState: any;
}

export interface ArtemisStatusUpdatePayload {
  statusupdate: string;
  userChangedby?: string;
  userChangedbyUniqueName?: string;
  userChangeddate: string;
}

const normalizeSurveyAttachmentMetadata = (
  attachments: IArtemisAttachment[]
): IArtemisAttachment[] =>
  attachments.map((attachment) => {
    const surveyMarker =
      attachment.gps_pipadolaunchinfoquestion ??
      attachment.attributes?.gps_pipadolaunchinfoquestion;

    return surveyMarker === SURVEY_ATTACHMENT_VALUE
      ? {
          ...attachment,
          gps_pipadolaunchinfoquestion: SURVEY_ATTACHMENT_VALUE,
        }
      : attachment;
  });

export interface ArtemisClosureStatementPayload {
  closurestatement: string;
  closurechangedby?: string;
  closurechangedbyUniqueName?: string;
  closurelastchangedate: string;
}
export interface IGPSMandatePickerError {
  gps_assignedto: string;
  gps_requestorsinfo: string;
  gps_pirmanager: string;
  gps_StrategyLead: string;
  gps_LaunchManagerName: string;
  gps_FinancePIRStakeholder: string;
  gps_CorpAccountingPIRStakeholder: string;
  gps_CELAPIRStakeholderName: string;
  gps_CompliancePIRStakeholder: string;
  gps_OperationsPIRStakeholderName: string;
}
export interface ICosellMandatePickerError {
  cosell_assignedto: string;
  cosell_microsoftowner: string;
  cosell_microsoftcoowner: string;
}
// Validation Automation : Start
export type IDisplayForm = 'NONE' | 'ADD' | 'EDIT';

// Interface for the validation automation slice state
export interface IValidationAutomationSlice {
  validationMessages: IValidationResponse;
  displayForm: IDisplayForm;
  editObject: IFieldValidation;
  editObjectIndex: number;
}
const validateInitialState: IValidationAutomationSlice = {
  validationMessages: {} as IValidationResponse,
  displayForm: 'NONE',
  editObject: {} as IFieldValidation,
  editObjectIndex: -1,
};
// Validation Automation : End
export interface IGetRaidDataSlice extends IValidationAutomationSlice {
  raidDetails: any;
  isGetRaidDataCallInProgress: boolean;
  isGetCommentsCallInProgress: boolean;
  isSurveyAttachmentUploadInProgress: boolean;
  initiArtemisData: IArtemisInitPayload;
  UpdatediArtemisData: IArtemisInitPayload;
  isFormDirty: boolean;
  commentList: object[];
  robDetailRHFContext: IROBDetailRHFContext;
  SGSDetailRHFContext: ISGSDetailRHFContext;
  updateActionApiStatus: UpdateActionApiStatus;
  isCreateCommentInProgress: boolean;
  isUpdateCommentInProgress: boolean;
  responseRaidData: boolean;
  artemisHistory: [];
  raidHistory: WorkItemUpdate[];
  isRaidHistoryLoading: boolean;
  workItemSubscriptions: boolean;
  isFollowing: boolean;
  insertNotes: boolean;
  closeAction: boolean;
  insertEmailTemplate: boolean;
  opportunityId: string;
  solutionArea: any;
  dealSize: string;
  notesData: string;
  gpsMandatePickerError: IGPSMandatePickerError;
  cosellMandatePickerError: ICosellMandatePickerError;
  // NBAR State
  initialNbarEditableFormData: INBARFormData;
  nbarEditableFormData: INBARFormData;
  nbarNonEditableFormData: INBARNonEditableFormData;
  isNbarEditableFormDirty: boolean;
  nbarWorkloadsTabelData: INBARWorkloadTableStoreData[];
  getWorkLoadsData: IGetWorkloadResponse;
  isFetchingWorkloadsData: boolean;
  recommendedOffers: IRecommendedOfferItem[];
  isFetchingRecommendedOffers: boolean;
  nbarFormValidation: INBARFormValidation;
  nbarFormMandateFields: INBARFormMandateFields;
  isNbarFieldDisabled: boolean;
}

const initialEditFormObject: INBARFormData = {
  nbarOwner: [],
  nbarScenario: '',
  nbarStatus: '',
  benchMarkTCOReportLink: '',
  nextBestAction: [],
  nextBestActionDetails: '',
  commitmentStatus: '',
  nbarSuggestedRevenue: '',
  nbarOwnerNotes: '',
  actionTaken: '',
  followUp: '',
  followUpTargetDate: null,
  originatingCaseId: '',
  nbarRejectionReason: '',
  reasonForDelayBeyond3Months: '',
  nbar_selectedWorkloads: [], //Maintaining for frontend code reference , No need to sent to backend as part of payload
};

const initialNbarValidationState: INBARFormValidation = {
  actionTaken: '',
  nbarStatus: '',
  nbarRejectionReason: '',
  nbarRejectionDetails: '',
}

const initialNbarMandateFields: INBARFormMandateFields = {
  actionTaken: false,
  nbarStatus: false,
  nbarRejectionReason: false,
  nbarRejectionDetails: false,
}

const initialState = {
  raidDetails: {},
  isGetRaidDataCallInProgress: true,
  isGetCommentsCallInProgress: false,
  isSurveyAttachmentUploadInProgress: false,
  isUpdateRaidActionCallInProgress: false,
  initiArtemisData: {},
  UpdatediArtemisData: {},
  isFormDirty: false,
  commentList: [],
  robDetailRHFContext: {},
  updateActionApiStatus: UpdateActionApiStatus.None,
  isCreateCommentInProgress: false,
  isUpdateCommentInProgress: false,
  responseRaidData: false,
  raidHistory: [],
  isRaidHistoryLoading: false,
  artemisTags: [],
  workItemSubscriptions: false,
  isFollowing: false,
  insertNotes: false,
  closeAction: false,
  insertEmailTemplate: false,
  opportunityId: '',
  solutionArea: [],
  dealSize: '',
  notesData: '',
  //Mandate persona picker fields : start [Once cosell and gps UI migrated to dynamic v2 it is not required]
  cosellMandatePickerError : {
    cosell_assignedto : '',
    cosell_microsoftowner : '',
    cosell_microsoftcoowner: '',
  },
  gpsMandatePickerError : {
    gps_assignedto : '',
    gps_requestorsinfo : '',
    gps_pirmanager: '',
    gps_StrategyLead:'',
    gps_LaunchManagerName:'',
    gps_FinancePIRStakeholder: '',
    gps_CorpAccountingPIRStakeholder: '',
    gps_CELAPIRStakeholderName: '',
    gps_CompliancePIRStakeholder: '',
    gps_OperationsPIRStakeholderName: '',
  },
  //Mandate persona picker fields : end

  // NBAR Initial State
  initialNbarEditableFormData: {...initialEditFormObject},
  nbarEditableFormData: {...initialEditFormObject},
  nbarFormValidation: {...initialNbarValidationState},
  nbarFormMandateFields: {...initialNbarMandateFields},
  isNbarFieldDisabled: false,
  nbarNonEditableFormData: {
    lxpRecommendedOffers: '',
  },
  getWorkLoadsData:{},
  isFetchingWorkloadsData: false,
  isNbarEditableFormDirty: false,
  recommendedOffers: [] as IRecommendedOfferItem[],
  isFetchingRecommendedOffers: false,
  nbarWorkloadsTabelData: [] as INBARWorkloadTableStoreData[],

  // Validation Automation : Start
  validationMessages: {} as IValidationResponse,
  displayForm: 'NONE',
  editObject: {} as IFieldValidation,
  editObjectIndex: -1,
  // Validation Automation : End
};

// Validation Automation : Start
const resetEditFormObject =  {
  editObject : {} as IFieldValidation,
  editObjectIndex : -1,
  displayForm : 'NONE',
};

//TODO : This is a workaround function to add additional fields to validation messages.
//It will be removed once backend support is added for additional fields in validation messages.
const additionalFieldsBasedOnOffer = (
  state: IGetRaidDataSlice,
  validationMessages: IValidationResponse
): IValidationResponse => {

  //If no fields exist, return as is
  if(validationMessages?.fields?.length === 0){
    return validationMessages;
  }

  const messageCloned = JSON.parse(JSON.stringify(validationMessages));

  const offerId = state?.raidDetails?.fields?.offerId;
  const isEcifOfferType = UATConstants.ECIFOffersKeysList.includes(offerId);

  //All Offer
  const ProjectStackingCheck = messageCloned.fields.find(
    (field) =>
      field.fieldname === UATConstants.ValidationAutomation.additionalValidtionField.ProjectStacking
  );
  if (!ProjectStackingCheck) {
    messageCloned.fields.push({
      'fieldname': UATConstants.ValidationAutomation.additionalValidtionField.ProjectStacking,
      'fieldvalue': '',
      'isvalid': '',
      'message': '',
      'comments': '',
      'ismanual': true,
    });
  }

  //ECIF Offers
  if(isEcifOfferType){
      const PartnerSpecializationCheck = messageCloned.fields.find(
        (field) =>
          field.fieldname ===
          UATConstants.ValidationAutomation.additionalValidtionField.PartnerSpecialization
      );
      if (!PartnerSpecializationCheck) {
        messageCloned.fields.push({
          'fieldname': UATConstants.ValidationAutomation.additionalValidtionField.PartnerSpecialization,
          'fieldvalue': '',
          'isvalid': '',
          'message': '',
          'comments': '',
          'ismanual': true,
        });
      }

      const MACCCheck = messageCloned.fields.find(
        (field) =>
          field.fieldname === UATConstants.ValidationAutomation.additionalValidtionField.MACCCheck
      );
      if (!MACCCheck) {
        messageCloned.fields.push({
          'fieldname': UATConstants.ValidationAutomation.additionalValidtionField.MACCCheck,
          'fieldvalue': '',
          'isvalid': '',
          'message': '',
          'comments': '',
          'ismanual': true,
        });
      }
  }

  return messageCloned;
};
// Validation Automation : End

export const manageActionCosmosSlice = createSlice({
  name: 'manageActionCosmosSlice',
  initialState,
  reducers: {
    initArtemisActionDetails: (state, action: PayloadAction<IArtemisInitPayload>) => {
      const normalizedPayload = {
        ...action.payload,
        attachments: normalizeSurveyAttachmentMetadata(action.payload.attachments),
      };
      return {
        ...state,
        initiArtemisData: {
          ...state.initiArtemisData,
          ...cloneDeep(normalizedPayload),
        },
        UpdatediArtemisData: {
          ...state.UpdatediArtemisData,
          ...cloneDeep(normalizedPayload),
        },
      };
    },
    initArtemisActionRelatedDetails: (state, action: PayloadAction<Related>) => {
      return {
        ...state,
        initiArtemisData: {
          ...state.initiArtemisData,
          relatedData: cloneDeep(action.payload),
        },
        UpdatediArtemisData: {
          ...state.UpdatediArtemisData,
          relatedData: cloneDeep(action.payload),
        },
      };
    },
    updateArtemisActionTagsDetails: (state, action: PayloadAction<string>) => {
      const tagsData = action.payload.toString();
      const reduxState = {
        ...state,
        UpdatediArtemisData: {
          ...state.UpdatediArtemisData,
          tags: tagsData,
        },
      };
      const isFormDirty = !isEqual(reduxState.initiArtemisData, reduxState.UpdatediArtemisData);
      reduxState['isFormDirty'] = isFormDirty;
      if (isFormDirty) {
        printDifference(reduxState);
      }
      return reduxState;
    },
    updateArtemisActionRecommendedTagsDetails: (
      state,
      action: PayloadAction<IRecommendedTags[]>
    ) => {
      const tagsData = action.payload;
      const reduxState = {
        ...state,
        UpdatediArtemisData: {
          ...state.UpdatediArtemisData,
          recommendedTags: tagsData,
        },
      };
      const isFormDirty = !isEqual(reduxState.initiArtemisData, reduxState.UpdatediArtemisData);
      reduxState['isFormDirty'] = isFormDirty;
      if (isFormDirty) {
        printDifference(reduxState);
      }
      return reduxState;
    },
    updateArtemisActionRelatedDetails: (state, action: PayloadAction<Related>) => {
      const reduxState = {
        ...state,
        UpdatediArtemisData: {
          ...state.UpdatediArtemisData,
          relatedData: action.payload,
        },
      };
      const isFormDirty = !isEqual(reduxState.initiArtemisData, reduxState.UpdatediArtemisData);
      reduxState['isFormDirty'] = isFormDirty;
      if (isFormDirty) {
        printDifference(reduxState);
      }
      return reduxState;
    },
    updateArtemisAttachmentDetails: (state, action: PayloadAction<IArtemisAttachment[]>) => {
      const normalizedAttachments = normalizeSurveyAttachmentMetadata(action.payload);
      const reduxState = {
        ...state,
        UpdatediArtemisData: {
          ...state.UpdatediArtemisData,
          attachments: normalizedAttachments,
        },
      };
      const isFormDirty = !isEqual(reduxState.initiArtemisData, reduxState.UpdatediArtemisData);
      reduxState['isFormDirty'] = isFormDirty;
      if (isFormDirty) {
        printDifference(reduxState);
      }
      return reduxState;
    },
    updateArtemisStatusUpdate: (state, action: PayloadAction<ArtemisStatusUpdatePayload>) => {
      const reduxState = {
        ...state,
        UpdatediArtemisData: {
          ...state.UpdatediArtemisData,
          statusupdate: action.payload.statusupdate,
          statusupdatechangedby: {
            displayname: action.payload.userChangedby || '',
            uniquename: action.payload.userChangedbyUniqueName || '',
          },
          userChangeddate: action.payload.userChangeddate,
        },
      };
      const isFormDirty = !isEqual(reduxState.initiArtemisData, reduxState.UpdatediArtemisData);
      reduxState['isFormDirty'] = isFormDirty;
      if (isFormDirty) {
        printDifference(reduxState);
      }
      return reduxState;
    },
    updateArtemisClosureStatement: (state, action: PayloadAction<ArtemisClosureStatementPayload>) => {
      const reduxState = {
        ...state,
        UpdatediArtemisData: {
          ...state.UpdatediArtemisData,
          closurestatement: action.payload.closurestatement,
          closurestmtchangedby: {
            displayname: action.payload.closurechangedby || '',
            uniquename: action.payload.closurechangedbyUniqueName || '',
          },
          closurelastchangedate: action.payload.closurelastchangedate,
        },
      };
      const isFormDirty = !isEqual(reduxState.initiArtemisData, reduxState.UpdatediArtemisData);
      reduxState['isFormDirty'] = isFormDirty;
      if (isFormDirty) {
        printDifference(reduxState);
      }
      return reduxState;
    },
    updateArtemisCommentStatement: (state, action: PayloadAction<string>) => {
      const reduxState = {
        ...state,
        UpdatediArtemisData: {
          ...state.UpdatediArtemisData,
          comment: action.payload,
        },
      };
      const isFormDirty = !isEqual(reduxState.initiArtemisData, reduxState.UpdatediArtemisData);
      reduxState['isFormDirty'] = isFormDirty;
      if (isFormDirty) {
        printDifference(reduxState);
      }
      return reduxState;
    },
    updateRobDetailRHFContext: (state, action: PayloadAction<IROBDetailRHFContext>) => {
      return {
        ...state,
        robDetailRHFContext: cloneDeep(action.payload),
      };
    },
    updateSGSDetailRHFContext: (state, action: PayloadAction<ISGSDetailRHFContext>) => {
      return {
        ...state,
        SGSDetailRHFContext: cloneDeep(action.payload),
      };
    },
    setUpdateRaidActionApiStatus: (state, action: PayloadAction<UpdateActionApiStatus>) => {
      state.updateActionApiStatus = action.payload;
    },
    commitUpdatedArtemisData: (state) => {
      state.initiArtemisData = cloneDeep(state.UpdatediArtemisData);
      state.isFormDirty = false;
    },
    setSurveyAttachmentUploadInProgress: (state, action: PayloadAction<boolean>) => {
      state.isSurveyAttachmentUploadInProgress = action.payload;
    },
    getComments: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        initiArtemisData: {
          ...state.initiArtemisData,
          commentList: _.cloneDeep(action.payload),
        },
        UpdatediArtemisData: {
          ...state.UpdatediArtemisData,
          commentList: _.cloneDeep(action.payload),
        },
      };
    },
    createComment: (state, action: PayloadAction<IRoBCommentItem>) => {
      state.commentList.push(action.payload);
    },
    updateComment: (state, action: PayloadAction<IRoBCommentItem>) => {
      // const index = state.commentList.findIndex(comment => comment.id === action.payload.id);
      // if (index !== -1) {
      //   state.commentList[index] = action.payload;
      // }
      state.commentList.push(action.payload);
    },
    updateArtemisNewCommentDetails: (state, action: PayloadAction<string>) => {
      const reduxState = {
        ...state,
        UpdatediArtemisData: {
          ...state.UpdatediArtemisData,
          comment: action.payload,
        },
      };
      const isFormDirty = !isEqual(reduxState.initiArtemisData, reduxState.UpdatediArtemisData);
      reduxState['isFormDirty'] = isFormDirty;
      if (isFormDirty) {
        printDifference(reduxState);
      }
      return reduxState;
    },
    updateIsInsertNotes: (state, action: PayloadAction<boolean>) => {
      state.insertNotes = action.payload;
    },
    updateIsCloseAction: (state, action: PayloadAction<boolean>) => {
      state.closeAction = action.payload;
    },
    updateIsInsertEmailTemplate: (state, action: PayloadAction<boolean>) => {
      state.insertEmailTemplate = action.payload;
    },
    updateOpportunityId: (state, action: PayloadAction<string>) => {
      state.opportunityId = action.payload;
    },
    updateSolutionArea: (state, action: PayloadAction<any>) => {
      state.solutionArea = action.payload;
    },
    updateDealSize: (state, action: PayloadAction<string>) => {
      state.dealSize = action.payload;
    },
    updateNotesData: (state, action: PayloadAction<string>) => {
      state.notesData = action.payload;
    },
    updateGPSMandatePickerError: (state, action: PayloadAction<Partial<IGPSMandatePickerError>>) => {
      state.gpsMandatePickerError = { ...state.gpsMandatePickerError, ...action.payload };
    },
    updateCosellMandatePickerError: (state, action: PayloadAction<Partial<ICosellMandatePickerError>>) => {
      state.cosellMandatePickerError = { ...state.cosellMandatePickerError, ...action.payload };
    },
    // Validation Automation : Start
    setDisplayForm: (state, action: { payload: IDisplayForm }) => {
      state.displayForm = action.payload;
    },
    resetAllAutomationValidateState: (state) => {
        return { ...state , ...validateInitialState };
    },
    setEditObject: (
      state,
      action: { payload: { editObject: IFieldValidation; editObjectIndex: number } }
    ) => {
      state.editObject = action.payload.editObject;
      state.editObjectIndex = action.payload.editObjectIndex;
      state.displayForm = 'EDIT';
    },
    resetEditObject: (state) => {
      return { ...state , ...resetEditFormObject };
    },
    // Validation Automation : End
    // NBAR Reducers
    setInitialNbarEditableFormData: (state, action: PayloadAction<INBARFormData>) => {
      state.initialNbarEditableFormData = {...action.payload};
      state.nbarEditableFormData = {...action.payload};
      state.isNbarEditableFormDirty = false;
    },
    updateNbarEditableFormField: (
      state,
      action: PayloadAction<{ fieldName: keyof INBARFormData; value: INBARFormData[keyof INBARFormData] }>
    ) => {
      const { fieldName, value } = action.payload;
      state.nbarEditableFormData = {
        ...state.nbarEditableFormData,
        [fieldName]: value,
      };
      state.isNbarEditableFormDirty = !isEqual(state.nbarEditableFormData, state.initialNbarEditableFormData);
    },
    batchUpdateNbarEditableFormField: (
      state,
      action: PayloadAction<{ fieldName: keyof INBARFormData; value: INBARFormData[keyof INBARFormData] }[]>
    ) => {
      action.payload.forEach(({ fieldName, value }) => {
        state.nbarEditableFormData = {
          ...state.nbarEditableFormData,
          [fieldName]: value,
        };
      });
      state.isNbarEditableFormDirty = !isEqual(state.nbarEditableFormData, state.initialNbarEditableFormData);
    },
    updateNbarNonEditableFormField: (
      state,
      action: PayloadAction<{ fieldName: keyof INBARNonEditableFormData; value: INBARNonEditableFormData[keyof INBARNonEditableFormData] }>
    ) => {
      const { fieldName, value } = action.payload;
      state.nbarNonEditableFormData = {
        ...state.nbarNonEditableFormData,
        [fieldName]: value,
      };
    },
    setNbarWorkloadsTableData: (state, action: PayloadAction<INBARWorkloadTableStoreData[]>) => {
      state.nbarWorkloadsTabelData = action.payload;
    },
    addNbarWorkloadsTableItem: (state, action: PayloadAction<INBARWorkloadTableStoreData[]>) => {
      state.nbarWorkloadsTabelData = [...action.payload, ...(state.nbarWorkloadsTabelData ?? [])];
      const newWorkloadNames = action.payload.map((item) => item.workload);
      state.nbarEditableFormData = {
        ...state.nbarEditableFormData,
        nbar_selectedWorkloads: [
          ...(state.nbarEditableFormData.nbar_selectedWorkloads ?? []),
          ...newWorkloadNames,
        ],
      };
      state.isNbarEditableFormDirty = true;
    },
    removeNbarWorkloadsTableItem: (
      state,
      action: PayloadAction<{ workload: string; source: string }>
    ) => {
      const { workload, source } = action.payload;
      state.nbarWorkloadsTabelData = (state.nbarWorkloadsTabelData ?? []).filter(
        (item) => !(item.workload === workload && item.source === source)
      );
    },
    setNbarRecommendedOffers: (state, action: PayloadAction<IRecommendedOfferItem[]>) => {
      state.recommendedOffers = action.payload;
    },
    updateNbarFormValidation: (
      state,
      action: PayloadAction<Partial<INBARFormValidation>>
    ) => {
      state.nbarFormValidation = {
        ...state.nbarFormValidation,
        ...action.payload,
      };
    },
    setNbarFormMandateFields: (
      state,
      action: PayloadAction<INBARFormMandateFields>
    ) => {
      state.nbarFormMandateFields = action.payload;
    },
    setIsNbarFieldDisabled: (state, action: PayloadAction<boolean>) => {
      state.isNbarFieldDisabled = action.payload;
    },
    initializeNbarState: (
      state,
      action: PayloadAction<{
        isDisabled: boolean;
        mandateFlags: INBARFormMandateFields;
        validation: INBARFormValidation;
      }>
    ) => {
      state.isNbarFieldDisabled = action.payload.isDisabled;
      state.nbarFormMandateFields = action.payload.mandateFlags;
      state.nbarFormValidation = {
        ...state.nbarFormValidation,
        ...action.payload.validation,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(getRaidEntityApi.endpoints.getRaidEntityData.matchPending, (state) => {
      state.isGetRaidDataCallInProgress = true;
    });
    builder.addMatcher(
      getRaidEntityApi.endpoints.getRaidEntityData.matchFulfilled,
      (state, action) => {
        state.raidDetails = action.payload.value[0] ?? {};
        state.isGetRaidDataCallInProgress = false;
        state.isFormDirty = false;
      }
    );
    builder.addMatcher(getRaidEntityApi.endpoints.getRaidItemById.matchRejected, (state) => {
      state.isGetRaidDataCallInProgress = false;
    });
    builder.addMatcher(getRaidEntityApi.endpoints.getRaidItemById.matchPending, (state) => {
      state.isGetRaidDataCallInProgress = true;
    });
    builder.addMatcher(
      getRaidEntityApi.endpoints.getRaidItemById.matchFulfilled,
      (state, action) => {
        // getRaidItemById can return a partial RAID entity (e.g., only raidid/id/pk).
        // Preserve the existing full details to avoid RBAC recalculations on incomplete data.
        state.raidDetails = {
          ...(state.raidDetails ?? {}),
          ...(action.payload ?? {}),
        };
        state.isGetRaidDataCallInProgress = false;
        state.isFormDirty = false;
      }
    );
    builder.addMatcher(getRaidEntityApi.endpoints.getRaidEntityData.matchRejected, (state) => {
      state.isGetRaidDataCallInProgress = false;
    });
    builder.addMatcher(getRaidEntityApi.endpoints.updateRaidEntityData.matchPending, (state) => {
      state.isUpdateRaidActionCallInProgress = true;
    });
    builder.addMatcher(
      getRaidEntityApi.endpoints.updateRaidEntityData.matchFulfilled,
      (state, action) => {
        state.raidDetails = action?.payload?.raidEntity
          ? action?.payload?.raidEntity
          : state.raidDetails;
        state.isUpdateRaidActionCallInProgress = false;
      }
    );
    builder.addMatcher(getRaidEntityApi.endpoints.updateRaidEntityData.matchRejected, (state) => {
      state.isUpdateRaidActionCallInProgress = false;
    });
    builder.addMatcher(getRaidEntityApi.endpoints.getComments.matchPending, (state) => {
      state.isGetCommentsCallInProgress = true;
    });

    builder.addMatcher(getRaidEntityApi.endpoints.getComments.matchFulfilled, (state, action) => {
      state.commentList = action.payload.value[0] ?? {};
      state.isGetCommentsCallInProgress = false;
    });

    builder.addMatcher(getRaidEntityApi.endpoints.getComments.matchRejected, (state) => {
      state.isGetCommentsCallInProgress = false;
    });

    builder.addMatcher(getRaidEntityApi.endpoints.createRaidComment.matchPending, (state) => {
      state.isCreateCommentInProgress = true;
    });
    builder.addMatcher(
      getRaidEntityApi.endpoints.createRaidComment.matchFulfilled,
      (state, action) => {
        if (action.payload && action.payload.value) {
          state.commentList = action.payload.value[0] ?? {};
        } else {
          state.commentList = {};
        }
        state.isCreateCommentInProgress = false;
      }
    );
    builder.addMatcher(getRaidEntityApi.endpoints.createRaidComment.matchRejected, (state) => {
      state.isCreateCommentInProgress = false;
    });

    builder.addMatcher(getRaidEntityApi.endpoints.updateRaidComments.matchPending, (state) => {
      state.isUpdateCommentInProgress = true;
    });
    builder.addMatcher(
      getRaidEntityApi.endpoints.updateRaidComments.matchFulfilled,
      (state, action) => {
        const updatedComments = action.payload; // Assuming action.payload contains the updated comments array
        state.commentList = updatedComments;
        state.isUpdateCommentInProgress = false;
      }
    );
    builder.addMatcher(getRaidEntityApi.endpoints.updateRaidComments.matchRejected, (state) => {
      state.isUpdateCommentInProgress = false;
    });
    builder.addMatcher(getRaidEntityApi.endpoints.getRaidHistory.matchPending, (state) => {
      state.isRaidHistoryLoading = true;
    });
    builder.addMatcher(
      getRaidEntityApi.endpoints.getRaidHistory.matchFulfilled,
      (state, action) => {
        state.raidHistory = (action.payload ? [...action.payload] : []).sort((a, b) => {
          return b.id - a.id;
        });
        state.isRaidHistoryLoading = false;
      }
    );
    builder.addMatcher(getRaidEntityApi.endpoints.getRaidHistory.matchRejected, (state) => {
      state.isRaidHistoryLoading = false;
    });
    builder.addMatcher(
      getRaidEntityApi.endpoints.getArtemisTags.matchFulfilled,
      (state, action) => {
        state.artemisTags = action.payload ? action.payload : [];
      }
    );
    builder.addMatcher(
      getRaidEntityApi.endpoints.getFollowing.matchFulfilled,
      (state, action: any) => {
        state.workItemSubscriptions = action.payload === '0000' ? false : true;
      }
    );
    // Add error matcher to handle failed requests
    builder.addMatcher(
      getRaidEntityApi.endpoints.getFollowing.matchRejected,
      (state) => {
        // Handle the error case here
        //console.error('getFollowing matchRejected:', action.error);
        // Set default state on error
        state.workItemSubscriptions = false;
      }
    );
    builder.addMatcher(getRaidEntityApi.endpoints.addFollowing.matchFulfilled, (state) => {
      state.workItemSubscriptions = true;
      state.isFollowing = true;
    });
    builder.addMatcher(getRaidEntityApi.endpoints.deleteFollowing.matchFulfilled, (state) => {
      state.workItemSubscriptions = false;
      state.isFollowing = false;
    });
    builder.addMatcher(getRaidEntityApi.endpoints.getWorkloads.matchFulfilled, (state, action) => {
      state.getWorkLoadsData = action.payload;
      state.isFetchingWorkloadsData = false;
    });
    builder.addMatcher(getRaidEntityApi.endpoints.getWorkloads.matchPending, (state) => {
      state.isFetchingWorkloadsData = true;
    });
    builder.addMatcher(getRaidEntityApi.endpoints.getWorkloads.matchRejected, (state) => {
      state.isFetchingWorkloadsData = false;
    });
    builder.addMatcher(getRaidEntityApi.endpoints.getRecommendedOffers.matchPending, (state) => {
      state.isFetchingRecommendedOffers = true;
    });
    builder.addMatcher(getRaidEntityApi.endpoints.getRecommendedOffers.matchFulfilled, (state, action) => {
      //recommendedoffers is Temporary one need to discuss with API Team
      state.recommendedOffers = action?.payload?.recommendedOffers ? action?.payload?.recommendedOffers : action?.payload?.recommendedoffers ? action?.payload?.recommendedoffers : [];
      state.isFetchingRecommendedOffers = false;
    });
    builder.addMatcher(getRaidEntityApi.endpoints.getRecommendedOffers.matchRejected, (state) => {
      state.isFetchingRecommendedOffers = false;
    });

    // Validation Automation : Start
    // Get validation messages
    builder.addMatcher(
      validationAutomationApi.endpoints.getValidationMessages.matchFulfilled,
      (state, action) => {
        if (action?.payload) {
          const validationMessages = action?.payload as IValidationResponse;
          const messageCloned = additionalFieldsBasedOnOffer(state,validationMessages);
          state.validationMessages = messageCloned;
        } else {
          const validationMessages = { fields: [] } as IValidationResponse;
          state.validationMessages = validationMessages;
        }
      }
    );
    builder.addMatcher(
      validationAutomationApi.endpoints.getValidationMessages.matchRejected,
      (state) => {
        return { ...state , ...validateInitialState };
      }
    );

    //Update validation messages
    builder.addMatcher(
      validationAutomationApi.endpoints.updateValidationMessage.matchFulfilled,
      (state, action) => {
        const validationMessages = action?.payload as IUpdateValidationResponse;
        const copiedMessages = {
          ...state.validationMessages,
          ...validationMessages, // Merge updated fields with get existing state
        };
        const messageCloned = additionalFieldsBasedOnOffer(state,copiedMessages);
        state.validationMessages = messageCloned;
        // Reset edit form object
        state.editObject = {} as IFieldValidation;
        state.editObjectIndex = -1;
        state.displayForm = 'NONE';
      }
    );
    // Validation Automation : End
  },
});

export default manageActionCosmosSlice.reducer;

//Utility functions to get difference between two objects
export const difference = (origObj: any, newObj: any) => {
  const changes = (newObj: any, origObj: any) => {
    let arrayIndexCounter = 0;
    return transform(newObj, function (result: any, value: any, key: any) {
      if (!isEqual(value, origObj[key])) {
        const resultKey = isArray(origObj) ? arrayIndexCounter++ : key;
        result[resultKey] =
          isObject(value) && isObject(origObj[key]) ? changes(value, origObj[key]) : value;
      }
    });
  };
  return changes(newObj, origObj);
};

export const printDifference = (reduxState) => {
  console.log(difference(reduxState.initiArtemisData, reduxState.UpdatediArtemisData));
};

export const {
  initArtemisActionDetails,
  updateArtemisActionTagsDetails,
  updateArtemisActionRecommendedTagsDetails,
  updateArtemisAttachmentDetails,
  updateArtemisActionRelatedDetails,
  updateArtemisClosureStatement,
  updateArtemisStatusUpdate,
  updateArtemisCommentStatement,
  updateRobDetailRHFContext,
  setUpdateRaidActionApiStatus,
  commitUpdatedArtemisData,
  setSurveyAttachmentUploadInProgress,
  getComments,
  createComment,
  updateComment,
  updateArtemisNewCommentDetails,
  updateSGSDetailRHFContext,
  updateIsInsertNotes,
  updateIsCloseAction,
  updateIsInsertEmailTemplate,
  updateOpportunityId,
  updateSolutionArea,
  updateDealSize,
  updateNotesData,
  updateGPSMandatePickerError,
  updateCosellMandatePickerError,
  // Validation Automation : Start
  setDisplayForm, 
  setEditObject, 
  resetEditObject,
  resetAllAutomationValidateState,
  // Validation Automation : End
  // NBAR Actions
  setInitialNbarEditableFormData,
  updateNbarEditableFormField,
  batchUpdateNbarEditableFormField,
  updateNbarNonEditableFormField,
  setNbarWorkloadsTableData,
  addNbarWorkloadsTableItem,
  removeNbarWorkloadsTableItem,
  setNbarRecommendedOffers,
  updateNbarFormValidation,
  setNbarFormMandateFields,
  setIsNbarFieldDisabled,
  initializeNbarState,
} = manageActionCosmosSlice.actions;

// Generated by GitHub Copilot | Updated on: 2026-05-12 | 2 lines modified | Removed nextBestAction from initial NBAR validation and mandate states
// Generated by GitHub Copilot | Updated on: 2026-05-20 | 1 lines modified | Changed nextBestAction initial value from string to array
// Generated by GitHub Copilot | Updated on: 2026-05-20 | 1 lines modified | Added followUpTargetDate to initial NBAR form state
// Generated by GitHub Copilot | Updated on: 2026-08-31 | 22 lines modified | Normalized survey attachment deletion flags
// Generated by GitHub Copilot | Updated on: 2026-09-01 | 12 lines modified | Normalized Survey attachment metadata
// Generated by GitHub Copilot | Updated on: 2026-09-01 | 4 lines modified | Tracked Survey attachment upload progress
// Generated by GitHub Copilot | Updated on: 2026-10-07 | 5 lines modified | Committed saved Artemis data as dirty baseline
// Copilot change tracking: 2026-11-01 migration: Retain frozen release reconciliation.
// Copilot change tracking: 2026-11-02 frontend: Remove unused Cosmos slice imports.
// Copilot change tracking: 2026-11-02 frontend-unused: Remove the ignored rejected-action reducer parameter.
